import { useState, useEffect, useRef } from 'react';
import { useWebsites } from '@/context/WebsitesContext';
import { useAuth } from '@/context/AuthContext';
import { useWordPress } from '@/context/WordPressContext';
import { supabase } from '@/integrations/supabase/client';

export default function useSecondStageOnboarding() {
  const { currentWebsite } = useWebsites();
  const { user } = useAuth();
  const { settings: wpSettings } = useWordPress();
  const [showModal, setShowModal] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  const lastLogRef = useRef<{ websiteId?: string; shouldShow?: boolean } | null>(null);

  useEffect(() => {
    const checkSecondStageOnboarding = async () => {
      if (!user || !currentWebsite?.id) {
        setIsChecking(false);
        return;
      }

      try {
        // Check if the user has completed second stage onboarding
        const { data, error } = await supabase
          .from('websites')
          .select('second_stage_onboarding_completed, second_stage_skipped, second_stage_dont_show_again, cms')
          .eq('id', currentWebsite.id)
          .single();

        if (error) {
          console.error('Error checking second stage onboarding status:', error);
          setIsChecking(false);
          return;
        }

        // Debug logging only when the computed outcome changes

        // Show modal if:
        // 1. Second stage onboarding is not completed, OR
        // 2. User skipped but WordPress is not connected (allow them to try again)
        // AND WordPress is not already connected
        // AND CMS is not already set to 'other_cms'
        // AND user hasn't chosen "don't show again"
        const shouldShowModal = (
          (!data?.second_stage_onboarding_completed || (data?.second_stage_skipped && !wpSettings?.is_connected)) &&
          !wpSettings?.is_connected && 
          data?.cms !== 'other_cms' &&
          !data?.second_stage_dont_show_again
        );
        
        // Only show automatically once per website (use localStorage guard)
        const shownKey = `second_stage_shown_${currentWebsite.id}`;
        const alreadyShown = localStorage.getItem(shownKey) === '1';

        const logKey = { websiteId: currentWebsite.id, shouldShow: shouldShowModal && !alreadyShown };
        if (!lastLogRef.current || lastLogRef.current.websiteId !== logKey.websiteId || lastLogRef.current.shouldShow !== logKey.shouldShow) {
          if (logKey.shouldShow) {
            console.log('Showing second stage onboarding modal - first automatic show for this website');
          } else {
            console.log('Second stage onboarding completed, WordPress connected, or CMS set to other_cms');
          }
          lastLogRef.current = logKey;
        }

        if (shouldShowModal && !alreadyShown) {
          setShowModal(true);
          try { localStorage.setItem(shownKey, '1'); } catch {}
        }
      } catch (error) {
        console.error('Error in checkSecondStageOnboarding:', error);
      } finally {
        setIsChecking(false);
      }
    };

    // Add a small delay to ensure the website context is loaded
    const timer = setTimeout(checkSecondStageOnboarding, 2000);

    return () => clearTimeout(timer);
  }, [user, currentWebsite?.id, wpSettings?.is_connected, currentWebsite?.cms]);

  const handleComplete = () => {
    setShowModal(false);
  };

  const handleClose = () => {
    setShowModal(false);
  };

  return {
    showModal,
    isChecking,
    handleComplete,
    handleClose,
    setShowModal
  };
}
